import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Plus, Edit, Trash2, Search, Image, Upload, Layers } from 'lucide-react';
import toast from 'react-hot-toast';
import { categoryService } from '../../services';

const CategoryMaster = () => {
  const [categories, setCategories] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm();

  // Load categories from API
  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const response = await categoryService.getAll({ search: searchTerm });
      setCategories(response.data || []);
    } catch (error) {
      console.error('Error loading categories:', error);
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  // Reload categories when search term changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadCategories();
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Remove filteredCategories since we're using API search now

  const openModal = (category = null) => {
    setEditingCategory(category);
    setShowModal(true);
    setImagePreview(category ? category.category_image : null);
    if (category) {
      reset({
        name: category.name,
        slug: category.slug,
        parent_id: category.parent_id,
        is_active: category.is_active
      });
    } else {
      reset({ name: '', slug: '', parent_id: '', is_active: true });
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingCategory(null);
    setImagePreview(null);
    reset();
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // In a real app, you'd upload this to your server/cloud storage
      // For now, we'll just create a preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      if (editingCategory) {
        // Update category
        await categoryService.update(editingCategory.id, data);
        toast.success('Category updated successfully!');
      } else {
        // Create new category
        await categoryService.create(data);
        toast.success('Category created successfully!');
      }
      closeModal();
      loadCategories(); // Reload the list
    } catch (error) {
      console.error('Error saving category:', error);
      toast.error('Failed to save category');
    } finally {
      setLoading(false);
    }
  };

  const deleteCategory = async (id) => {
    if (window.confirm('Are you sure you want to delete this category?')) {
      try {
        setLoading(true);
        await categoryService.delete(id);
        toast.success('Category deleted successfully!');
        loadCategories(); // Reload the list
      } catch (error) {
        console.error('Error deleting category:', error);
        toast.error('Failed to delete category');
      } finally {
        setLoading(false);
      }
    }
  };

  const toggleStatus = async (category) => {
    try {
      setLoading(true);
      await categoryService.update(category.id, { 
        name: category.name,
        slug: category.slug,
        parent_id: category.parent_id,
        is_active: !category.is_active 
      });
      toast.success('Category status updated!');
      loadCategories(); // Reload the list
    } catch (error) {
      console.error('Error updating category status:', error);
      toast.error('Failed to update category status');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="card">
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px'
        }}>
          <h1 style={{ fontSize: '24px', fontWeight: '600', color: '#111827' }}>
            Category Master
          </h1>
          <button
            onClick={() => openModal()}
            className="btn btn-primary"
          >
            <Plus size={18} />
            Add Category
          </button>
        </div>

        {/* Search */}
        <div style={{ marginBottom: '20px', position: 'relative', maxWidth: '300px' }}>
          <Search
            size={18}
            style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#6b7280'
            }}
          />
          <input
            type="text"
            placeholder="Search categories..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-box"
            style={{ paddingLeft: '40px' }}
          />
        </div>

        {/* Categories Table */}
        <div style={{ overflowX: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Category Name</th>
                <th>Slug</th>
                <th>Parent</th>
                <th>Status</th>
                <th>Created Date</th>
                <th>Updated Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {categories.map((category) => (
                <tr key={category.id}>
                  <td>{category.id}</td>
                  <td style={{ fontWeight: '500' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Layers size={16} color="#6b7280" />
                      {category.name}
                    </div>
                  </td>
                  <td>
                    <code style={{
                      fontFamily: 'monospace',
                      backgroundColor: '#f3f4f6',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      fontSize: '12px'
                    }}>
                      {category.slug}
                    </code>
                  </td>
                  <td>{category.parent?.name || '-'}</td>
                  <td>
                    <button
                      onClick={() => toggleStatus(category)}
                      className={`status-badge ${category.is_active ? 'status-active' : 'status-inactive'}`}
                      style={{ cursor: 'pointer', border: 'none' }}
                      disabled={loading}
                    >
                      {category.is_active ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td>{new Date(category.created_at).toLocaleDateString()}</td>
                  <td>{new Date(category.updated_at).toLocaleDateString()}</td>
                  <td>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => openModal(category)}
                        className="btn btn-secondary"
                        style={{ padding: '6px 8px', fontSize: '12px' }}
                      >
                        <Edit size={14} />
                      </button>
                      <button
                        onClick={() => deleteCategory(category.id)}
                        className="btn btn-danger"
                        style={{ padding: '6px 8px', fontSize: '12px' }}
                        disabled={loading}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {loading && (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            color: '#6b7280'
          }}>
            Loading categories...
          </div>
        )}

        {!loading && categories.length === 0 && (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            color: '#6b7280'
          }}>
            No categories found.
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">
                {editingCategory ? 'Edit Category' : 'Add New Category'}
              </h2>
              <button onClick={closeModal} className="close-btn">
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="form-group">
                <label className="form-label">Category Name</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Enter category name"
                  {...register('name', {
                    required: 'Category name is required',
                    minLength: {
                      value: 2,
                      message: 'Category name must be at least 2 characters'
                    }
                  })}
                />
                {errors.name && (
                  <div className="error-message">{errors.name.message}</div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Category Slug</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Enter category slug (e.g. lehenga, dupatta)"
                  {...register('slug', {
                    required: 'Category slug is required',
                    minLength: {
                      value: 2,
                      message: 'Category slug must be at least 2 characters'
                    },
                    pattern: {
                      value: /^[a-z0-9-]+$/,
                      message: 'Slug can only contain lowercase letters, numbers, and hyphens'
                    }
                  })}
                />
                {errors.slug && (
                  <div className="error-message">{errors.slug.message}</div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Category Image</label>
                
                {/* Image Preview */}
                {imagePreview && (
                  <div style={{
                    marginBottom: '12px',
                    textAlign: 'center'
                  }}>
                    <img
                      src={imagePreview}
                      alt="Preview"
                      style={{
                        width: '120px',
                        height: '120px',
                        objectFit: 'cover',
                        borderRadius: '8px',
                        border: '2px solid #e5e7eb'
                      }}
                    />
                  </div>
                )}

                {/* File Input */}
                <div style={{
                  border: '2px dashed #d1d5db',
                  borderRadius: '8px',
                  padding: '20px',
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'border-color 0.2s'
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.target.style.borderColor = '#3b82f6';
                }}
                onDragLeave={(e) => {
                  e.target.style.borderColor = '#d1d5db';
                }}
                >
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    style={{ display: 'none' }}
                    id="category-image"
                  />
                  <label htmlFor="category-image" style={{ cursor: 'pointer' }}>
                    <Upload size={32} color="#6b7280" style={{ marginBottom: '8px' }} />
                    <p style={{ color: '#6b7280', margin: 0 }}>
                      Click to upload or drag and drop
                    </p>
                    <p style={{ color: '#9ca3af', fontSize: '12px', margin: '4px 0 0' }}>
                      PNG, JPG, GIF up to 10MB
                    </p>
                  </label>
                </div>

                {/* URL Input Alternative */}
                <div style={{ marginTop: '12px' }}>
                  <input
                    type="url"
                    className="form-input"
                    placeholder="Or enter image URL"
                    {...register('category_image')}
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Status</label>
                <select
                  className="form-select"
                  {...register('is_active', { required: 'Status is required' })}
                >
                  <option value={true}>Active</option>
                  <option value={false}>Inactive</option>
                </select>
                {errors.is_active && (
                  <div className="error-message">{errors.is_active.message}</div>
                )}
              </div>

              <div style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end',
                marginTop: '24px'
              }}>
                <button
                  type="button"
                  onClick={closeModal}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Saving...' : editingCategory ? 'Update Category' : 'Create Category'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryMaster;
